import React from 'react';

import { Card, CardProps } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconRemove } from '@components/icons/remove';
import {
  IntegrationEndpointMinimal,
  IntegrationWebhookMinimal,
} from '@components/integrations/integrations-types';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

import { BASE_ACTION_TYPE, JourneyAction } from 'api/types';
import {
  getIntegrationEndpoint,
  getIntegrationWebhook,
} from 'ui/authenticated/main/pages/journeys/journey-node-utils';

import { ActionIcon, getActionLabel } from '../action-config';

type ActionData = { action: BASE_ACTION_TYPE; values?: JourneyAction['values'] };

export const ignoreTypes: BASE_ACTION_TYPE[] = [
  'journey_merge',
  'journey_start',
  'journey_branch',
  'branch_base',
  'journey_loop',
  'wait_for_time',
];

type SmallActionCardProps = {
  actionData: ActionData;
  integrations: IntegrationEndpointMinimal[];
  webhooks: IntegrationWebhookMinimal[];
  description?: string;
  variant?: CardProps['variant'];
  onRemove?: () => void;
  removeIcon?: React.ReactNode;
};

export const SmallActionCard = ({
  actionData,
  integrations,
  webhooks,
  description,
  variant,
  onRemove,
  removeIcon = <IconRemove />,
}: SmallActionCardProps) => {
  const displayInfo = getActionDisplayInfo(actionData, integrations, webhooks);

  return (
    <Card spaceX={4} spaceY={2} variant={variant}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          {displayInfo.icon}
          <div>
            <Text font="semibold14" text={displayInfo.name} />
            {description && <Text font="regular14" color="textSecondary" text={description} />}
          </div>
        </Stack>
        {onRemove && <IconButton onClick={onRemove} icon={removeIcon} variant="ghost-purple" />}
      </Stack>
    </Card>
  );
};

export const getActionDisplayInfo = (
  action: ActionData,
  integrations: IntegrationEndpointMinimal[],
  webhooks: IntegrationWebhookMinimal[],
): { name: string; icon: React.ReactNode } => {
  const integrationData =
    action.action === 'integration'
      ? getIntegrationEndpoint(integrations, action as JourneyAction<'integration'>)
      : action.action === 'webhook'
        ? getIntegrationWebhook(webhooks, action as JourneyAction<'webhook'>)
        : undefined;

  if (integrationData) {
    return {
      name: integrationData.name,
      icon: integrationData.thumbnail ? (
        <SquareImage src={integrationData.thumbnail} />
      ) : (
        <ActionIcon action={action.action} />
      ),
    };
  }

  return {
    name: getActionLabel(action.action),
    icon: <ActionIcon action={action.action} />,
  };
};

export function DirectLine() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="4" viewBox="0 0 428 4" fill="none">
      <path
        d="M426 2L2 2.00004"
        stroke={theme.color.purple20}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function RelationshipLine() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="2" viewBox="0 0 426 2" fill="none">
      <path
        d="M425 1L1 1.00004"
        stroke={theme.color.orange100}
        strokeLinecap="round"
        strokeDasharray="16 16"
      />
    </svg>
  );
}

export const getRelationshipOptions = (
  actions: (ActionData & { shortId: string; description?: string })[],
  integrations: IntegrationEndpointMinimal[],
  webhooks: IntegrationWebhookMinimal[],
  noDescriptionText: string,
) => {
  return actions.map((action) => {
    const displayInfo = getActionDisplayInfo(action, integrations, webhooks);
    return {
      value: action.shortId,
      helpText: action.description || noDescriptionText,
      filterText: `${displayInfo.name} ${action.description ?? ''}`,
      label: (
        <Stack>
          <Stack space={2} orientation="horizontal" align="center">
            {displayInfo.icon}
            {displayInfo.name}
          </Stack>
        </Stack>
      ),
    };
  });
};
